import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";

const logo =
  "https://cdn.outfall.website/Images/Logo/OutFall%20Logo%20Orange.png";

const DEFAULT_TABS = {
  hero: true,
  stats: true,
  news: true,
  play: true,
  modes: true,

  page_news: true,
  page_tracker: true,
  page_careers: true,
  page_status: true,
  page_staff_team: true,
};

function getDiscordId(user) {
  return (
    user?.discord_id ||
    user?.discordId ||
    user?.id ||
    user?.user?.id ||
    user?.discord?.id ||
    null
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [discordUser, setDiscordUser] = useState(null);
  const [staffPermissions, setStaffPermissions] = useState({});
  const [tabs, setTabs] = useState(DEFAULT_TABS);

  const isTabEnabled = useMemo(() => {
    return (key) => tabs[key] !== false;
  }, [tabs]);

  useEffect(() => {
    loadTabs();

    try {
      const storedUser =
        localStorage.getItem("outfall_discord_user") ||
        localStorage.getItem("discord_user") ||
        localStorage.getItem("user");

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setDiscordUser(parsedUser);
      } else {
        setDiscordUser(null);
      }
    } catch (err) {
      console.error("[Header] Failed to read Discord user:", err);
      setDiscordUser(null);
    }
  }, []);

  async function loadTabs() {
    try {
      const { data, error } = await supabase
        .from("Tabs")
        .select("tab_key, enabled");

      if (error) throw error;

      const nextTabs = { ...DEFAULT_TABS };

      if (Array.isArray(data)) {
        data.forEach((tab) => {
          if (tab?.tab_key) {
            nextTabs[tab.tab_key] = tab.enabled === true;
          }
        });
      }

      setTabs(nextTabs);
    } catch (err) {
      console.error("[Header] Failed to load tabs:", err);
      setTabs(DEFAULT_TABS);
    }
  }

  useEffect(() => {
    async function checkStaffAccess() {
      const discordId = getDiscordId(discordUser);

      if (!discordId) {
        setStaffPermissions({});
        return;
      }

      try {
        const res = await fetch("/api/check-staff-role", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            discord_id: discordId,
          }),
        });

        const data = await res.json();
        setStaffPermissions(data.permissions || {});
      } catch (err) {
        console.error("[Header] Failed to check staff role:", err);
        setStaffPermissions({});
      }
    }

    checkStaffAccess();
  }, [discordUser]);

  const hostname =
    typeof window !== "undefined"
      ? window.location.hostname.replace("www.", "")
      : "";

  const isStatusDomain =
    hostname === "status.outfall.co.uk" ||
    hostname === "status.outfall.website";

  const mainDomain =
    hostname === "status.outfall.co.uk"
      ? "https://outfall.co.uk"
      : hostname === "status.outfall.website"
        ? "https://outfall.website"
        : "";

  const statusHref =
    hostname === "outfall.co.uk"
      ? "https://status.outfall.co.uk"
      : hostname === "outfall.website"
        ? "https://status.outfall.website"
        : isStatusDomain
          ? "/"
          : "/status";

  const links = useMemo(() => {
    const navLinks = [];

    if (isTabEnabled("page_news")) {
      navLinks.push({
        label: "News",
        href: isStatusDomain ? `${mainDomain}/news` : "/news",
      });
    }

    if (isTabEnabled("modes")) {
      navLinks.push({
        label: "Modes",
        href: isStatusDomain ? `${mainDomain}/home#modes` : "/home#modes",
      });
    }

    if (isTabEnabled("page_careers")) {
      navLinks.push({
        label: "Careers",
        href: isStatusDomain ? `${mainDomain}/careers` : "/careers",
      });
    }

    if (isTabEnabled("page_staff_team")) {
      navLinks.push({
        label: "Our Team",
        href: isStatusDomain ? `${mainDomain}/our-team` : "/our-team",
      });
    }

    if (isTabEnabled("page_status")) {
      navLinks.push({
        label: "Status",
        href: statusHref,
      });
    }

    if (isTabEnabled("page_tracker")) {
      navLinks.push({
        label: "Tracker",
        href: isStatusDomain ? `${mainDomain}/tracker` : "/tracker",
      });
    }

    if (isTabEnabled("play")) {
      navLinks.push({
        label: "Play",
        href: isStatusDomain ? `${mainDomain}/home#play` : "/home#play",
      });
    }

    return navLinks;
  }, [isStatusDomain, mainDomain, statusHref, isTabEnabled]);

  const homeHref = isStatusDomain ? `${mainDomain}/home` : "/home";
  const playHref = isStatusDomain ? `${mainDomain}/home#play` : "/home#play";

  const staffPages = useMemo(() => {
    const pages = [];

    if (staffPermissions.admin) {
      pages.push({
        label: "Admin Panel",
        href: isStatusDomain ? `${mainDomain}/admin` : "/admin",
      });

      pages.push({
        label: "Manage Site",
        href: isStatusDomain ? `${mainDomain}/manage` : "/manage",
      });
    }

    if (staffPermissions.careers && isTabEnabled("page_careers")) {
      pages.push({
        label: "Careers Review",
        href: isStatusDomain
          ? `${mainDomain}/careers-review`
          : "/careers-review",
      });

      pages.push({
        label: "Careers Create",
        href: isStatusDomain
          ? `${mainDomain}/careers-create`
          : "/careers-create",
      });
    }

    return pages;
  }, [staffPermissions, isStatusDomain, mainDomain, isTabEnabled]);

  return (
    <header className="fixed left-0 top-0 z-50 w-full border-b border-[#ff8f6b]/20 bg-black/75 backdrop-blur-xl">
      <div className="relative flex h-[76px] w-full items-center justify-between px-5 md:px-10">
        <a href={homeHref} className="z-10 flex items-center">
          <img
            src={logo}
            alt="Outfall"
            className="h-16 w-auto object-contain md:h-20"
          />
        </a>

        <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 md:flex">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="font-black uppercase text-white transition hover:text-[#ffd978]"
            >
              {link.label}
            </a>
          ))}

          {staffPages.length > 0 && (
            <div className="group relative flex h-[76px] items-center">
              <button
                type="button"
                className="font-black uppercase text-white transition hover:text-[#ffd978]"
              >
                Staff
              </button>

              <div className="invisible absolute left-1/2 top-[58px] z-50 w-56 -translate-x-1/2 rounded-2xl border border-[#ff8f6b]/25 bg-black/95 p-2 opacity-0 shadow-[0_0_25px_rgba(255,143,107,0.22)] backdrop-blur-xl transition group-hover:visible group-hover:opacity-100">
                {staffPages.map((page) => (
                  <a
                    key={page.href}
                    href={page.href}
                    className="block rounded-xl px-4 py-3 text-sm font-black uppercase text-white transition hover:bg-[#ffd978] hover:text-black"
                  >
                    {page.label}
                  </a>
                ))}
              </div>
            </div>
          )}
        </nav>

        <div className="z-10 hidden w-[150px] justify-end md:flex">
          {isTabEnabled("play") ? (
            <a
              href={playHref}
              className="outfall-button inline-flex whitespace-nowrap"
            >
              Play Free
            </a>
          ) : (
            <span className="invisible inline-flex whitespace-nowrap rounded-xl px-5 py-3 font-black">
              Play Free
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="z-10 flex h-11 w-11 items-center justify-center rounded-lg border border-[#ff8f6b]/30 bg-white/10 text-3xl font-black leading-none text-white md:hidden"
          aria-label="Open menu"
        >
          {open ? "×" : "☰"}
        </button>
      </div>

      <div
        className={`md:hidden ${
          open ? "block" : "hidden"
        } border-t border-[#ff8f6b]/20 bg-black/95 px-5 py-6`}
      >
        <nav className="flex flex-col gap-5">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="text-lg font-black uppercase text-white transition hover:text-[#ffd978]"
            >
              {link.label}
            </a>
          ))}

          {staffPages.length > 0 && (
            <div className="border-t border-[#ff8f6b]/20 pt-5">
              <p className="mb-3 text-sm font-black uppercase text-[#ffd978]">
                Staff
              </p>

              <div className="flex flex-col gap-3">
                {staffPages.map((page) => (
                  <a
                    key={page.href}
                    href={page.href}
                    onClick={() => setOpen(false)}
                    className="rounded-xl border border-[#ff8f6b]/20 bg-white/5 px-4 py-3 text-sm font-black uppercase text-white transition hover:bg-[#ffd978] hover:text-black"
                  >
                    {page.label}
                  </a>
                ))}
              </div>
            </div>
          )}

          {isTabEnabled("play") && (
            <a
              href={playHref}
              onClick={() => setOpen(false)}
              className="outfall-button mt-2 w-fit"
            >
              Play Free
            </a>
          )}
        </nav>
      </div>
    </header>
  );
}
